<script setup>
import { ref, getCurrentInstance, onMounted, onUnmounted, watch } from 'vue'
import emitter from '../utils/emitter'
import { notifyDialog, notifyDialogError } from '../composables/useDialogNotice'
import { DriverStore } from '../pinia/Driver.js'

const props = defineProps({
    initialCode: {
        type: String,
        default: ''
    }
})

const { proxy } = getCurrentInstance()
const Driver = DriverStore()
const code = ref(props.initialCode)
const useds = ref([])

watch(() => props.initialCode, (newValue) => {
    if (newValue) {
        code.value = newValue
    }
})

getUsed()

onMounted(() => {
    emitter.on('setRedeemCode', (value) => {
        code.value = value
    })
})

onUnmounted(() => {
    emitter.off('setRedeemCode')
})

async function getUsed() {
    try {
        const res = await proxy.$api({ serviceName: "account", methodName: "codeUsed" })
        useds.value = res
    } catch (err) {
        console.error('Error loading used codes:', err)
    }
}

async function refreshUser() {
    try {
        const result = await proxy.$api({ serviceName: 'session', methodName: 'GetUserInfo', parameters: [] })
        if (!result?.user) return
        Driver.setUser(result.user)
        if (result.persona) Driver.setPersona(result.persona)
        if (result.crew) Driver.setCrew(result.crew)
        if (result.others !== undefined) Driver.setOthers(result.others)
    } catch {}
}

async function save() {
    if (!code.value || code.value.trim().length === 0) {
        notifyDialog({ type: 'error', message: 'Please enter a code' })
        return
    }

    try {
        const response = await proxy.$http.post("", JSON.stringify({
            serviceName: "account",
            methodName: "redeemcode",
            parameters: [code.value]
        }))

        if (response.data.state == "error") {
            notifyDialog({ type: 'error', message: response.data.message || 'Failed to redeem code' })
        } else {
            code.value = ""
            notifyDialog({ type: 'success', message: 'Code redeemed successfully!' })
            await getUsed()
            await refreshUser()
            emitter.emit('refresh-wheelspins')
        }
    } catch (err) {
        notifyDialogError(err, 'Failed to redeem code')
    }
}
</script>

<template><div class="settings-tab-page redeemForm">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-wallet-giftcard</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Redeem Code</h3>
            <p class="page-subtitle">Enter your promotional code</p>
        </div>
    </div>

    <v-form>
        <div class="section-list">
            <div class="section-row">
                <div class="section-body">
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-ticket-confirmation</v-icon>
                                Promotional Code
                            </div>
                            <div class="setting-description">
                                Type your code here to claim your reward
                            </div>
                            <v-text-field v-model="code" placeholder="Enter code" variant="outlined" density="comfortable" hide-details class="setting-input code-input"></v-text-field>
                        </div>
                    </div>
                    <v-btn class="save-btn redeem-btn" variant="elevated" color="primary" size="large" @click="save" block>
                        <v-icon class="mr-2">mdi-gift</v-icon>
                        Redeem Code
                    </v-btn>
                </div>
            </div>
        </div>

        <div class="settings-tab-header history-header">
            <div class="header-icon">
                <v-icon size="28">mdi-history</v-icon>
            </div>
            <div class="header-content">
                <h3 class="page-title">Redeemed Codes</h3>
                <p class="page-subtitle">Your code history</p>
            </div>
        </div>

        <div class="section-list">
            <div class="section-row">
                <div class="codes-list">
                    <div v-if="useds.length === 0" class="empty-state">
                        <v-icon size="48" color="primary" class="mb-2">mdi-inbox</v-icon>
                        <div class="empty-text">No codes redeemed yet</div>
                    </div>
                    <div v-else class="code-item" v-for="used in useds" :key="used.code">
                        <div class="code-icon">
                            <v-icon color="primary">mdi-check-circle</v-icon>
                        </div>
                        <div class="code-details">
                            <div class="code-name">{{ used.code }}</div>
                            <div class="code-description">{{ used.name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </v-form>
</div></template>

<style scoped>
.code-input {
  text-transform:uppercase}
.redeem-btn {
  margin-top:4px}
.history-header {
  margin-top:24px}
.codes-list {
  max-height:250px;
  overflow-y:auto;
  overflow-x:hidden}
.codes-list::-webkit-scrollbar {
  width:6px}
.codes-list::-webkit-scrollbar-track {
  background:var(--glass);
  border-radius:3px}
.codes-list::-webkit-scrollbar-thumb {
  background:rgba(var(--v-theme-primary),.3);
  border-radius:3px}
.codes-list::-webkit-scrollbar-thumb:hover {
  background:rgba(var(--v-theme-primary),.5)}
.empty-state {
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:40px 20px;
  color:var(--faint)}
.empty-text {
  font-size:.9em}
.code-item {
  display:flex;
  align-items:center;
  gap:12px;
  padding:16px;
  background:var(--glass);
  border-bottom:1px solid var(--line);
  transition:background .2s ease}
.code-item:hover {
  background:var(--glass-strong)}
.code-item:last-child {
  border-bottom:none}
.code-icon {
  flex-shrink:0}
.code-details {
  flex:1}
.code-name {
  font-size:1em;
  font-weight:600;
  color:var(--ink);
  margin-bottom:4px;
  font-family:monospace}
.code-description {
  font-size:.85em;
  color:var(--mute)}
</style>
